import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  create,
  exportToCsv,
  fetchAll,
  fetchOne,
  findDuplicates,
  importFromCsv,
  remove,
  update,
} from '.';
import type { CreateRequestDto, ImportRequestsDto, Request, UpdateRequestDto } from './types';

type CreateRequestQueryDto = {
  projectId: string;
  data: CreateRequestDto;
};

type UpdateRequestQueryDto = {
  projectId: string;
  requestId: string;
  data: UpdateRequestDto;
};

type RemoveRequestQueryDto = {
  requestId: string;
  projectId: string;
};

export const useGetRequests = (id: string, query: Record<string, string>) => {
  return useQuery({
    queryKey: ['requests', 'project', id, query],
    queryFn: () => fetchAll(id, query),
  });
};

export const useGetRequest = (id: string, projectId: string) => {
  return useQuery({
    queryKey: ['projects', projectId, 'requests', id],
    queryFn: () => fetchOne(id, projectId),
  });
};

export const useCreateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, projectId }: CreateRequestQueryDto) => {
      return create(projectId, data);
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'stats', projectId] });
    },
  });
};

export const useUpdateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, data }: UpdateRequestQueryDto) => {
      return update(requestId, data);
    },
    onSuccess: (_, { requestId, projectId, data }) => {
      queryClient.setQueryData<Request>(
        ['projects', projectId, 'requests', requestId],
        (oldData) => {
          if (oldData) {
            return { ...oldData, ...data };
          }
        },
      );
      queryClient.invalidateQueries({ queryKey: ['requests', 'project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'stats', projectId] });
    },
  });
};

export const useRemoveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId }: RemoveRequestQueryDto) => remove(requestId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'stats', projectId] });
    },
  });
};

export const useFindDuplicates = (id: string) => {
  return useQuery({
    queryKey: ['requests', 'duplicates', 'project', id],
    queryFn: () => findDuplicates(id),
    enabled: false,
  });
};

export const useExportRequestsToCsv = () => {
  return useMutation({
    mutationFn: (projectId: string) => exportToCsv(projectId),
  });
};

type ImportRequestsQueryDto = {
  projectId: string;
  data: ImportRequestsDto;
};

export const useImportRequests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: ImportRequestsQueryDto) => importFromCsv(projectId, data),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['requests', 'project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'stats', projectId] });
    },
  });
};
